import { useState, useEffect } from 'react';

import { t, getIntlLocale } from '@i18n';

// MUI x-charts feeds the series color into d3-color (brighter()/hover), which
// can't parse `var(...)`, so CSS-var colors must be resolved to a hex string at
// runtime before they reach a chart. Shared by every chart in this folder.
export function useResolvedCssColor(varName) {
    const [color, setColor] = useState(() => {
        if (typeof window === 'undefined') return undefined;
        return getComputedStyle(document.documentElement)
            .getPropertyValue(varName).trim() || undefined;
    });
    useEffect(() => {
        if (typeof window === 'undefined') return;
        const value = getComputedStyle(document.documentElement)
            .getPropertyValue(varName)
            .trim();
        if (value) setColor(value);
    }, [varName]);
    return color;
}

export function moneySymbol(currency = 'BRL') {
    return String(currency).toUpperCase() === 'USD' ? 'US$' : 'R$';
}

export function formatMoneyCompact(value, currency = 'BRL') {
    const symbol = moneySymbol(currency);
    const abs = Math.abs(value);
    if (abs >= 1_000_000) return `${symbol}${(value / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
    if (abs >= 1_000) return `${symbol}${(value / 1_000).toFixed(1).replace(/\.0$/, '')}k`;
    return `${symbol}${value.toLocaleString(getIntlLocale(), { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

export function formatMoneyFull(value, currency = 'BRL') {
    return `${moneySymbol(currency)} ${value.toLocaleString(getIntlLocale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function formatNumberCompact(value) {
    const abs = Math.abs(value);
    if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
    if (abs >= 1_000) return `${(value / 1_000).toFixed(1).replace(/\.0$/, '')}k`;
    return String(value);
}

// "12 vendas" / "1 venda" — a count paired with its unit noun. The default nouns
// are resolved on every call, never frozen at import time.
export function formatCountFull(value, singular, plural) {
    const n = Number(value) || 0;
    const one = singular || t('dashboard:charts.unit.item');
    const many = plural || t('dashboard:charts.unit.items');
    const noun = n === 1 ? one : many;
    return `${n.toLocaleString(getIntlLocale())} ${noun}`;
}

// ─── Time-bucket labels ───────────────────────────────────────────────────────
// Shared by every chart that plots a sales-report time series ({ date: { from,
// to }, value } buckets). Month and granularity names are keys, not labels: they
// are resolved at call time so axes and tooltips follow the active language.

const MONTH_KEYS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

function monthLabel(index) {
    return t(`dashboard:months.short.${MONTH_KEYS[index]}`);
}

const GRANULARITY_LABELS = {
    hour: 'dashboard:charts.granularity.hour',
    day: 'dashboard:charts.granularity.day',
    week: 'dashboard:charts.granularity.week',
    month: 'dashboard:charts.granularity.month',
    year: 'dashboard:charts.granularity.year',
};

// Upper bound on how many x-axis labels we let MUI render. More than this
// crowds the axis and forces MUI's auto-decimation, which is uneven for
// band scales with repeating labels (e.g. hourly buckets across many days).
const MAX_VISIBLE_LABELS = 12;

function pad(n) {
    return String(n).padStart(2, '0');
}

export function bucketLabel(point, granularityType, hourSpansDays) {
    if (!point?.date?.from) return '';
    const d = new Date(point.date.from.replace(' ', 'T'));
    switch (granularityType) {
        case 'hour':
            // Across multiple days, the tick interval thins labels down to
            // midnight buckets — show those as dates so the day boundary is
            // legible. Non-midnight buckets keep the bare hour for the
            // single-day case.
            if (hourSpansDays) return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}`;
            return `${pad(d.getHours())}h`;
        case 'day':
        case 'week':
            return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}`;
        case 'month':
            return `${monthLabel(d.getMonth())}/${String(d.getFullYear()).slice(-2)}`;
        case 'year':
            return String(d.getFullYear());
        default:
            return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}`;
    }
}

// Picks which bucket indexes get an x-axis label. For hour granularity
// spanning many days, snap to midnight indexes so labels land on day
// boundaries; otherwise stride evenly across the data.
export function computeTickLabelInterval(points, granularityType) {
    if (points.length <= MAX_VISIBLE_LABELS) return undefined;

    if (granularityType === 'hour') {
        const midnights = [];
        for (let i = 0; i < points.length; i++) {
            const from = points[i]?.date?.from;
            if (!from) continue;
            if (new Date(from.replace(' ', 'T')).getHours() === 0) midnights.push(i);
        }
        if (midnights.length > 1) {
            const stride = Math.max(1, Math.ceil(midnights.length / MAX_VISIBLE_LABELS));
            const visible = new Set();
            for (let i = 0; i < midnights.length; i += stride) visible.add(midnights[i]);
            return (_v, idx) => visible.has(idx);
        }
    }

    const stride = Math.max(1, Math.ceil(points.length / MAX_VISIBLE_LABELS));
    return (_v, idx) => idx % stride === 0;
}

export function bucketTooltipLabel(point, granularityType, amount) {
    if (!point?.date?.from) return '';
    const from = new Date(point.date.from.replace(' ', 'T'));
    const to = point?.date?.to ? new Date(point.date.to.replace(' ', 'T')) : null;
    const unit = t(GRANULARITY_LABELS[granularityType] || 'dashboard:charts.granularity.period');

    if (amount > 1 && to) {
        return `${pad(from.getDate())}/${pad(from.getMonth() + 1)} — ${pad(to.getDate())}/${pad(to.getMonth() + 1)}`;
    }
    if (granularityType === 'month') {
        return t('dashboard:charts.monthOfYear', {
            month: monthLabel(from.getMonth()),
            year: from.getFullYear(),
        });
    }
    if (granularityType === 'year') {
        return String(from.getFullYear());
    }
    if (granularityType === 'hour') {
        return `${pad(from.getDate())}/${pad(from.getMonth() + 1)} ${pad(from.getHours())}h`;
    }
    return `${pad(from.getDate())}/${pad(from.getMonth() + 1)}/${from.getFullYear()} (${unit})`;
}
